import { CircularProgress, Collapse } from "@mui/material";
import Joi from 'joi';
import { useEffect, useState } from 'react';
import { FcCollapse, FcExpand } from "react-icons/fc";
import { IoArrowBackSharp } from "react-icons/io5";
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Row } from "reactstrap";
import ButtonComponent from '../../components/ButtonComponent';
import ErrorMessageComponent from '../../components/ErrorMessageComponent';
import InputComponent from '../../components/InputComponent';
import Layout from '../../components/Layout/Layout';
import SearchSelectComponent from "../../components/SearchSelectComponent";
import { SelectItemsProps } from '../../components/Select2Component';
import SelectComponent from '../../components/SelectComponent';
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Toast from '../../helpers/Toast';
import { SearchByCompanyNameDTO } from "../../models/Subcontractor/SearchByCompanyNameDTO";
import { RoleDTO } from '../../models/User/RoleDTO';
import { RoleListCheckbox } from '../../models/User/RoleListCheckboxDTO';
import { UserCreateDTO } from '../../models/User/UserCreateDTO';
import { AdminService } from '../../services/AdminService';
import { SubcontractorService } from "../../services/SubcontractorService";
import { UserService } from "../../services/UserService";
import "../../styles/page.css";

export default function UserCreate() {

    const intl = useIntl();
    const navigate = useNavigate();
    const adminService = new AdminService();
    const userService = new UserService();
    const { id } = useParams<{ id: string; }>();
    const [roleList, setRoleList] = useState<RoleListCheckbox[]>([]);

    //Loadings
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingCreate, setLoadingCreate] = useState<boolean>(false);

    //Variáveis para controlar os collapses e expands das secções
    const [userIsOpen, setUserIsOpen] = useState<boolean>(true);

    const [inputName, setInputName] = useState<string>("");
    const [inputEmail, setInputEmail] = useState<string>("");
    const [inputActive, setInputActive] = useState<string>("");
    const [technicianCodeInput, setTechnicianCodeInput] = useState<string>("");
    const [preferedView, setPreferedView] = useState<string>("");

    const [listPreferedViews, setListPreferedViews] = useState<SelectItemsProps[]>([]);

    const [validationResult, setValidationResult] = useState<any[]>([]);

    const subcontractorService = new SubcontractorService();
    const [subcontractorSelected, setSubcontractorSelected] = useState<SelectItemsProps | null>(null);
    const [subcontractors, setSubcontractors] = useState<SelectItemsProps[]>([]);

    const { availableModules, assistanceTypes } = useAvailableModulesContext();
    const [isActiveOutsourcingModule, setIsActiveOutsourcingModule] = useState<boolean>(false);

    useEffect(() => {
        if (availableModules.includes("IsActiveOutsourcingModule")) {
            setIsActiveOutsourcingModule(true);
        }
    }, [availableModules]);

    const schemaUserValidation = Joi.object({
        name: Joi.string().required().messages({
            'any.required': intl.formatMessage({ id: "Screens.Auth.Validation.NameRequired" }),
            'string.base': intl.formatMessage({ id: "Screens.Auth.Validation.NameRequired" }),
            'string.empty': intl.formatMessage({ id: "Screens.Auth.Validation.NameRequired" }),
        }),
        email: Joi.string().email({ tlds: { allow: false } }).required().messages({
            'string.email': intl.formatMessage({ id: "Screens.Auth.Validation.EmailFormat" }),
            'any.required': intl.formatMessage({ id: "Screens.Auth.Validation.EmailRequired" }),
            'string.base': intl.formatMessage({ id: "Screens.Auth.Validation.EmailRequired" }),
            'string.empty': intl.formatMessage({ id: "Screens.Auth.Validation.EmailRequired" }),
        }),
        active: Joi.string().required().messages({
            'any.required': intl.formatMessage({ id: "Screens.UserEdit.Validation.UserStatus" }),
            'string.base': intl.formatMessage({ id: "Screens.UserEdit.Validation.UserStatus" }),
            'string.empty': intl.formatMessage({ id: "Screens.UserEdit.Validation.UserStatus" }),
        })
    });

    useEffect(() => {
        loadRoles();
        loadPreferedViews();
        setLoading(false);
    }, [])

    const goBack = () => {
        navigate(-1);
    }

    const loadRoles = async () => {
        var response = await adminService.GetRoles();
        if (response.success === true && response.obj != null) {
            var items: RoleListCheckbox[] = [];

            response.obj.forEach(s => {
                items.push({
                    name: s.name === "Técnico Oficina" ? 'Técnico' : s.name,
                    id: s.id,
                    isChecked: false
                });
            });
            setRoleList(items);
        }
    }

    const statusList: SelectItemsProps[] = [
        {
            label: intl.formatMessage({ id: "Screens.Users.Table.Active" }),
            value: "True"
        },
        {
            label: intl.formatMessage({ id: "Screens.Users.Table.Inactive" }),
            value: "False"
        },
    ]

    const createUser = () => {
        setLoadingCreate(true);

        validateUser(inputName, inputEmail, inputActive).then(async (result: any) => {
            if (result.error != null) {
                setValidationResult(result.error.details);
                setLoadingCreate(false);
            } else {
                setValidationResult([]);

                //Filtar na role list os que não tem o isChecked a true, e depois mapear para RoleDTO
                const roleListHelper = roleList?.filter((i) => i.isChecked !== false).map((obj) => (({ "id": obj.id, "name": obj.name }) as RoleDTO))

                const createUser: UserCreateDTO = {
                    id: id ?? '',
                    name: inputName,
                    email: inputEmail,
                    active: inputActive === "True" ? true : false,
                    preferedView: preferedView,
                    technicianCode: technicianCodeInput === "" ? null : technicianCodeInput,
                    userRoles: roleListHelper ? roleListHelper : [],
                    subcontractorId: subcontractorSelected != null ? Number(subcontractorSelected.value) : null
                }

                const response = await adminService.CreateUser(createUser);

                if (response.success !== true) {
                    Toast.Show("error", response.message);
                    setLoadingCreate(false);
                    return;
                }

                Toast.Show("success", response.message);
                setLoadingCreate(false);
                navigate(-1);
            }

        })
    }

    const getSubcontractors = async (
        companyName: string
    ): Promise<SelectItemsProps[]> => {
        var companyNameDTO: SearchByCompanyNameDTO = {
            companyName: companyName
        };
        const response = await subcontractorService.GetByCompanyName(companyNameDTO);
        if (response.success === false || response.obj === null) {
            Toast.Show("error", response.message);
        }
        const list: SelectItemsProps[] = [];
        response.obj?.forEach((element) => {
            var item: SelectItemsProps = {
                label: `${element.companyName}`,
                value: `${element.id}`
            }
            list.push(item);
        });
        setSubcontractors(list);
        return list;
    };

    const validateUser = (name: string, email: string, active: string) => {
        return new Promise(function (resolve) {
            var validationResult = schemaUserValidation.validate(
                {
                    name: name,
                    email: email,
                    active: active
                }, { abortEarly: false });
            resolve(validationResult);
        });
    }

    const handleRoleCheckbox = (e: any, i: number) => {
        let newList: RoleListCheckbox[] = roleList;

        newList?.map(i => {
            if (i.id === e.target.value) {
                i.isChecked = !i.isChecked;
            }
            return i;

        })
        setRoleList(newList?.filter((i) => i.id !== "-1"));
    }

    const loadPreferedViews = async () => {
        var resultGetPreferedViews = await userService.GetPreferedViews();

        if (resultGetPreferedViews.success == false) {
            Toast.Show("error", resultGetPreferedViews.message);
            return;
        }

        var items: SelectItemsProps[] = [];

        resultGetPreferedViews.obj.forEach(element => {
            items.push({
                label: element === 'Oficina' ? 'Técnico' : 'Backoffice',
                value: element
            });
        });

        setListPreferedViews(items);
    }

    return (
        <Layout>
            <div className="pageContainer">

                <div className="pageHeader">
                    <Row style={{ margin: "0px", padding: "0px" }}>
                        <Col style={{ margin: "0px", padding: "0px" }}>
                            <span className="pageTitle"> <IoArrowBackSharp className="pageTitleBackIcon" onClick={goBack} /><FormattedMessage id="Screens.UserCreate.Title" />:</span>
                        </Col>
                    </Row>
                </div>

                <div className="pageBody">

                    {loading === true && (
                        <div className="loading">
                            <span> <FormattedMessage id="Common.Loading.DataLoading" />...  </span><CircularProgress style={{ marginLeft: "10px" }} size={"25px"} />
                        </div>
                    )}

                    {loading === false && roleList != null && (
                        <div>
                            <div className="pageComponent">
                                <Row style={{ margin: "0px", padding: "0px" }}>
                                    <span className="pageSubHeaderBlockGray" onClick={() => setUserIsOpen(!userIsOpen)} ><FormattedMessage id="Screens.UserCreate.SubTitle" /><div className="CollapseIcon"> {userIsOpen ? <FcCollapse /> : <FcExpand />}</div></span>
                                    <Collapse in={userIsOpen} unmountOnExit>
                                        <Row style={{ marginBottom: "15px" }}>
                                            <InputComponent xl={3} lg={4} md={5} sm={12} xs={12} style={{ width: "100%" }} type={"text"} value={inputName} label={intl.formatMessage({ id: "Screens.User.Info.Name" })} readOnly={false} onChange={(t: string) => setInputName(t)} />
                                            <InputComponent xl={4} lg={4} md={5} sm={12} xs={12} style={{ width: "100%" }} type={"text"} value={inputEmail} label={intl.formatMessage({ id: "Screens.User.Info.Email" })} readOnly={false} onChange={(t: string) => setInputEmail(t)} />
                                            <SelectComponent xl={4} lg={4} md={5} sm={12} xs={12} style={{ width: "100%" }} selectedItemValue={preferedView} label={intl.formatMessage({ id: "Screens.User.Info.VistaPreferida" })} items={listPreferedViews} onChange={(t: string) => setPreferedView(t)} />
                                            <InputComponent xl={3} lg={4} md={5} sm={12} xs={12} style={{ width: "100%" }} type={"text"} value={technicianCodeInput} label={intl.formatMessage({ id: "Screens.User.Info.TechnicianCode" })} readOnly={false} onChange={(t: string) => setTechnicianCodeInput(t)} />
                                            {isActiveOutsourcingModule && (
                                                <>
                                                    <SearchSelectComponent
                                                        xl={4}
                                                        lg={4}
                                                        md={5}
                                                        sm={12}
                                                        xs={12}
                                                        label={intl.formatMessage({
                                                            id: "Screens.Users.Info.Subcontractor",
                                                        })}
                                                        loadOptions={getSubcontractors}
                                                        onChange={(e) => {
                                                            setSubcontractorSelected(e);
                                                        }}
                                                        value={subcontractorSelected}
                                                        style={{ width: "100%" }}
                                                    />
                                                </>
                                            )}
                                        </Row>
                                        {validationResult.length !== 0 &&
                                            <Row>
                                                <Col xl={3} lg={4} md={5} sm={12} xs={12}>
                                                    <ErrorMessageComponent validationResult={validationResult} fieldToValidate="name" />

                                                </Col>
                                                <Col xl={4} lg={4} md={5} sm={12} xs={12}>
                                                    <ErrorMessageComponent validationResult={validationResult} fieldToValidate="email" />
                                                </Col>
                                            </Row>
                                        }

                                        <Row style={{ marginBottom: "15px" }}>
                                            <SelectComponent xl={2} lg={4} md={5} sm={12} xs={12} style={{ width: "100%" }} label={intl.formatMessage({ id: "Screens.User.Info.Status" })} selectedItemValue={inputActive} items={statusList} onChange={(t: string) => { setInputActive(t) }} />
                                        </Row>

                                        {validationResult.length !== 0 &&
                                            <Row>
                                                <Col xl={2} lg={4} md={5} sm={12} xs={12}>
                                                    <ErrorMessageComponent validationResult={validationResult} fieldToValidate="active" />
                                                </Col>
                                            </Row>
                                        }

                                        <span className="label" ><FormattedMessage id="Screens.User.Info.Roles" />:</span>

                                        <div className="form-check">
                                            {roleList?.map((item, i) => {
                                                return (
                                                    <li key={item.id}>
                                                        <label className={item.isDisabled === true ? "form-check-label-disabled" : "form-check-label"}>
                                                            <input type="checkbox" disabled={item.isDisabled} key={item.id} value={item.id} checked={item.isChecked} onChange={(e) => handleRoleCheckbox(e, i)} className="form-check-input" />
                                                            {item.name}
                                                        </label>
                                                    </li>
                                                )
                                            })}
                                        </div>
                                        <Row style={{ marginTop: "1em" }}>
                                            <Col xl={1} lg={1} md={1} sm={1} xs={1}>
                                                <ButtonComponent disabled={loadingCreate} loading={loadingCreate} text={intl.formatMessage({ id: "Screens.User.Button.CreateUser" })} onClick={createUser} />
                                            </Col>
                                        </Row>
                                    </Collapse>
                                </Row>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout >
    )
}